<template>
  <div id="app">
    <div id="title">COVID-19, CO<sub>2</sub> &amp; CO</div>

    <div class="pageloader is-right-to-left" v-bind:class="{ 'is-active': !data_loaded }">
      <div class="loading"><img src="./assets/cov.png"> <br /> fetching data ...</div>
   </div>

    <WorldMap :data="data.world" :day="day" :date="date" v-if="data_loaded"/>

    <div id="credits">
      A work in progress by <a href="https://www.fooviz.xyz">FOOVIZ.XYZ</a>
      |  Sources: <a href="https://github.com/CSSEGISandData/COVID-19">JH CCSE</a>,
        <a href="https://www.esrl.noaa.gov/gmd/ccgg/trends/gl_data.html">NOAA/ESRL</a>,
        <a href="https://www.yahoo.com/">Yahoo</a>
      | Updates: daily</div>

    <div id="legend" v-if='data.totals.confirmed > 0'>

      <SparkLine :data="data.sums.active" :highlight="day" />
      <svg width="20px" height="20px"><circle cx=10 cy=10 r=10 fill="#aaa" opacity=".4" /></svg>
      <span class="text">active / day</span>
      <span class="number">{{ daily_active }}</span><br />
      <SparkLine :data="data.sums.new" :highlight="day" />
      <svg width="20px" height="20px"><circle cx=10 cy=10 r=10 fill="red" opacity=".7" /></svg>
      <span class="text">new / day</span>
      <span class="number">{{ daily_new }}</span><br />
      <SparkLine :data="data.sums.deaths" :highlight="day" />
      <svg width="20px" height="20px"><circle cx=10 cy=10 r=10 fill="black" opacity=".9" /></svg>
      <span class="text">deaths / day</span>
      <span class="number">{{ daily_death }}</span><br />
      <hr />
      <SparkLine :data="data.sums.confirmed" :width="50" :highlight="day"/>
      <span class="text">TOTAL</span>
      <span class="number">{{ daily_total_confirmed }}</span> <br />
    </div>

    <div id="dateslider">
      <vue-slider
        v-model="day"
        v-if="dates"
        tooltip='none'
        :min="0"
        :max="(dates.length -1)"
        @change="updateDay"
      ></vue-slider>
      <div class="max">
        {{ date_end }}
      </div>
      <div class="min">
        {{ date_start }}
      </div>

    </div>
    <div id="date" class="textoverlay" v-if='date'>{{  display_date }}</div>

    <div id="co2chart" class="linechart">
      <Co2Chart v-if="data.co2.length" :data="data.co2" :date="date" :width="320" :height="180"/>
    </div>
    <div id="dowchart" class="linechart" v-if="data.dow.length">
      Dow Jones
      <LineChart v-if="data.dow.length" :data="data.dow" :date="date" />
    </div>
    <div id="oilchart" class="linechart" v-if="data.dow.length">
      Crude Oil
      <LineChart v-if="data.oil.length" :data="data.oil" :date="date" />
    </div>

    <div class="brand"><a href="https://www.fooviz.xyz/">
      <img src="https://www.fooviz.xyz/img/logo.svg" width="150" />
      </a>
    </div>
  </div>


</template>

<script>
import WorldMap from './components/WorldMap.vue'
import Co2Chart from './components/CO2Chart.vue'
import LineChart from './components/LineChart.vue'
import SparkLine from './components/SparkLine.vue'
import * as d3 from 'd3'
import VueSlider from 'vue-slider-component'
import 'vue-slider-component/theme/default.css'

export default {
  name: 'App',
  components: {
    WorldMap, LineChart, Co2Chart, SparkLine,
    VueSlider
  },

  data() {
    return {
      animate: false,
      delay: 250,
      dates:0,
      day: 0,
      data: {
        world : [],
        co2 : [],
        co2vals: {},
        dow : [],
        oil : [],
        totals: {
          deaths: 0,
          active: 0,
          confirmed: 0
        },
        sums: {
          deaths: [],
          active: [],
          new: [],
          confirmed: [],
        }
      },
      data_loaded: false,
    };
  },

  computed: {
    date: function () {
      return d3.timeParse("%m/%d/%y")(this.dates[this.day])
    },
    display_date: function () {
      return d3.timeFormat("%Y-%m-%d")(this.date)
    },
    date_start: function () {
      return d3.timeFormat("%m-%d")(d3.timeParse("%m/%d/%y")(this.dates[0]))
    },
    date_end: function () {
      return d3.timeFormat("%m-%d")(d3.timeParse("%m/%d/%y")(this.dates[this.dates.length - 1]))
    },
    daily_death() {
      return d3.format(",d")(this.data.sums.deaths[this.day])
    },
    daily_new() {
      return d3.format(",d")(this.data.sums.new[this.day])
    },
    daily_active() {
      return d3.format(",d")(this.data.sums.active[this.day])
    },
    daily_total_confirmed() {
      return d3.format(",d")(this.data.sums.confirmed[this.day])
    },
    total_cases() {
      return d3.format(",d")(this.data.totals.confirmed)
    }
  },

  mounted () {
    this.loadData();

  },

  methods: {
    animateFrame() {
      if (this.day >= this.dates.length - 1) {
        clearInterval(this.animate);
        setTimeout(this.startAnimate, 3000)
      } else {
        this.day++;
      }
    },

    startAnimate() {
      this.day =0;
      this.animate = setInterval(this.animateFrame, this.delay);
    },

    updateDay() {
      clearInterval(this.animate);
    },

    loadData() {
      var self = this;

      d3.csv("data/time_series_covid19_confirmed_global.csv", function(d) {
        d.values = Object.values(d).slice(4).map(function(d) {return +d });
        //d.confirmed = d.values;
        d.new_confirmed = [ d.values[0] ]
        d.confirmed = [ d.values[0] ]
        self.data.totals.confirmed += d.values.slice(-1)[0]
        for (var i=1;i<d.values.length;i++){
          d.new_confirmed.push(d.values[i] - d.values[i-1])
          d.confirmed.push(+d.values[i])
        }
        return d;
      }).then( function(d) {

        self.data.world = d
        self.dates = Object.keys(self.data.world[0]).slice(4).slice(0,-3);
        d3.csv("data/time_series_covid19_deaths_global.csv", function(d, x) {
          d.values = Object.values(d).slice(4).map(function(d) {return +d });
          self.data.world[x].deaths = d.values ;
          self.data.world[x].new_deaths = [ d.values[0] ];
          self.data.totals.deaths += d.values.slice(-1)[0]
          for (var i=1;i<d.values.length;i++){
            self.data.world[x].new_deaths.push(Math.max(0, d.values[i] - d.values[i-1]))
          }
          return d;
        }).then(  function() {
          d3.csv("data/time_series_covid19_recovered_global.csv", function(d, x ) {
            d.values = Object.values(d).slice(4).map(function(d) {return +d });
            self.data.world[x].recovered = d.values;
            self.data.world[x].new_recovered= [ d.values[0] ]
            self.data.world[x].active = [ self.data.world[x].confirmed[0] ]
            self.data.totals.active += self.data.world[x].confirmed.slice(-1)[0]  - d.values.slice(-1)[0]
            for (var i=1;i<d.values.length;i++){
              self.data.world[x].new_recovered.push(Math.max(0, d.values[i] - d.values[i-1]))
              self.data.world[x].active.push(
                self.data.world[x].confirmed[i] - self.data.world[x].recovered[i] -  self.data.world[x].deaths[i]
              )
            }
            return d;
          }).then( function() {
            self.day = self.dates.length - 10;

            self.data.sums.deaths = self.data.world[0].new_deaths.slice();
            self.data.sums.new = self.data.world[0].new_confirmed.slice();
            self.data.sums.confirmed = self.data.world[0].confirmed.slice();
            self.data.sums.active = self.data.world[0].active.slice();

            self.data.world.forEach( function(w) {
              w.new_deaths.forEach( function(d, i) {
                self.data.sums.deaths[i] += d;
              })
              w.new_confirmed.forEach( function(d, i) {
                self.data.sums.new[i] += d;

              })
              w.confirmed.forEach( function(d, i) {
                self.data.sums.confirmed[i] += d;
              })
              w.active.forEach( function(d, i) {
                self.data.sums.active[i] += d;
              })
              self.data_loaded = true;

            })

            /*d3.csv("data/av_DJI.csv", function(d ) {
              //d['date'] =d3.timeParse("%Y-%m-%d")(d.Date);
              d['date'] =d3.timeParse("%Y-%m-%d")(d.timestamp);
              d['value'] = +d.close;
              return d;
            }).then(function(d) {
              console.log(d);
              self.data.dow = d.filter( function(d) { return d.date >= d3.timeParse("%m/%d/%y")(self.dates[0]) } )
              self.data.dow = self.data.dow.reverse().filter(function(d) { return d.close > 0 })
            });

            d3.csv("data/av_CL.csv", function(d) {
              d['date'] =d3.timeParse("%Y-%m-%d")(d.timestamp);
              d.value = +d.close;
              return d;
            }).then(function(d) {
              self.data.oil = d.filter( function(d) { return d.date >= d3.timeParse("%m/%d/%y")(self.dates[0]) } )
              self.data.oil = self.data.oil.reverse().filter(function(d) { return d.close > 0 })
            });
            */

            d3.json("data/y_DJI.json").then(function(d) {
              var dow = []
              d.chart.result[0].indicators.quote[0].close.forEach( function(c, i) {
                  dow.push( {
                    'date': d3.timeParse("%s")(d.chart.result[0].timestamp[i]),
                    close: d.chart.result[0].indicators.quote[0].close[i],
                    value: d.chart.result[0].indicators.quote[0].close[i]
                  })
              })
              self.data.dow = dow.filter( function(d) { return d.date >= d3.timeParse("%m/%d/%y")(self.dates[0]) } )
              self.data.dow = self.data.dow.filter(function(d) { return d.close > 0 })
            });

            d3.json("data/y_OIL.json").then(function(d) {
              var oil = []
              d.chart.result[0].indicators.quote[0].close.forEach( function(c, i) {
                  oil.push( {
                    'date': d3.timeParse("%s")(d.chart.result[0].timestamp[i]),
                    close: d.chart.result[0].indicators.quote[0].close[i],
                    value: d.chart.result[0].indicators.quote[0].close[i]
                  })
              })
              self.data.oil = oil.filter( function(d) { return d.date >= d3.timeParse("%m/%d/%y")(self.dates[0]) } )
              self.data.oil = self.data.oil.filter(function(d) { return d.close > 0 })
            });

            d3.csv("data/co2_trend_gl.csv", function(d) {
              var date = d3.timeParse("%Y-%m-%d")(d.year + "-" + d3.format('02d')(d.month) + "-" + d3.format('02d')(d.day));
              if (date >= self.dates[0])
                self.data.co2vals[d.year + "-" + d3.format('02d')(d.month) + "-" + d3.format('02d')(d.day)] = d.cycle;
              d.value = + d.cycle
              d['date'] = date;
              return d;
            }).then(function(d) {
              self.data.co2 = d.slice(- (self.dates.length + 800))
              self.animate = setInterval(self.animateFrame, self.delay);
            });
          })
        })
      })

    },

  }
}


</script>

d
<style>
@import "./assets/fonts/fonts.css";

html, body, #app, #map {
    padding:0; margin:0;
    height: 100%;
    width: 100%;
    font-family:'Arial'
  }
.v-slider { margin:0px auto; width:500px}

#title {
  position:absolute; top:0;left: 50%; transform: translate(-50%,0);z-index:4001;
  font-size:2.5em; font-family:'Charactron';
}
#dateslider {
  position:absolute; top:60px; width:33%; left: 50%; transform: translate(-50%,0);z-index:1003;
  font-family:'Charactron';font-size:10px;
}
#dateslider .max { float:right}
#date {position:absolute; top:75px;left: 50%; transform: translate(-50%,0);z-index:999;}

.linechart {position:absolute; bottom:30px;left: 50%; transform: translate(-50%,0);
  background:rgba(255,255,255,0.9); opacity: 0.8; padding:0px 5px 0px 5px;
  font-family:'Charactron';font-size:1em; text-align:center;
  z-index:1001}

.textoverlay {
  background:rgba(255,255,255,0.5); opacity: 0.8; padding:0px 5px 0px 5px;
  font-family:'Charactron';font-size:2em;
}

#co2chart {bottom:25px; background:none; opacity: 0.8;z-index:999;padding:0px 15px 0px 15px; }
#dowchart {transform: translate(-200%,-5%); bottom:35px; background:none; padding-right:10px; opacity: 0.6;z-index:999; border-right:1px solid #666}
#oilchart {transform: translate(100%,-5%); bottom:35px; background:none; padding-left:10px; opacity: 0.6;z-index:999; border-left:1px solid #666}

#legend {
    position:absolute; bottom:30px;left: 30px; z-index:1000; font-family: Arial; font-size:12px; line-height: 30px;
    border: 1px solid #999; padding:10px; width:250px;
    background: rgba(200,200,200,0.3)}
#legend svg {vertical-align:middle; margin: 0px 5px}
#legend .number {font-family:'Charactron';float:right;font-size:1.3em;line-height:25px}
#legend .text {margin-right:10px }
#legend hr {border:0px; border-bottom:1px solid #333; margin-top:15px}

.brand {position:absolute; right: 20px;top: 20px; z-index:1000; opacity:0.9}

#credits { font:10px/1.5 Arial, Helvetica, sans-serif;
  position:absolute; padding: 0px 5px; bottom:0px; left: 0;
  background:white; opacity: 0.7; z-index:1000}
.leaflet-control-attribution {
  opacity: 0.7;
}
a, a:visited, .leaflet-control-attribution a {color:#333; text-decoration:underline}

.pageloader { display:none; position: absolute; top:0px; left:0px; height:100%;width:100%;z-index:4000;background:white}
.pageloader .loading {position: absolute; left:50%; top: 50%; transform: translate(-50%,-50%); text-align:center; font-family:'Charactron'; }
.pageloader .loading img {-webkit-animation:spin 4s linear infinite;
    -moz-animation:spin 4s linear infinite;
    animation:spin 4s linear infinite;
}
@-moz-keyframes spin { 100% { -moz-transform: rotate(360deg); } }
@-webkit-keyframes spin { 100% { -webkit-transform: rotate(360deg); } }

.is-active { display:block;}

.vue-slider-process { background:#999}

@media(max-width:1024px) {
  #credits {}
}

@media(max-width:1024px) and (min-width:769px) {
  #legend {top:25px;height:150px;left:25px}
}

@media(max-width:768px) and (min-width:421px) {
    #legend {top:65px;height:150px;left:25px}

}

@media(max-width:420px) {
  #legend { left:50%; transform: translate(-50%,0); bottom:20px}
  .leaflet-control-attribution {display:none;}
}


</style>
